"use client";

import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";
import { fieldVariantClasses } from "./field-variants";

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

/* ─── Trigger ─── */
interface SelectTriggerProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
    "className" | "style"
  > {
  label?: string;
  error?: string;
  variant?: "violet" | "sky" | "amber" | "emerald" | "rose" | "slate" | "subtle";
  size?: "default" | "filter";
  floating?: boolean;
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ children, label, error, variant = "subtle", size = "default", floating = false, ...props }, ref) => {
  const [hasValue, setHasValue] = React.useState(false);
  const colors = fieldVariantClasses(variant, { error: Boolean(error) });
  const isFilter = size === "filter";

  return (
    <div className="relative w-full">
      <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
          "group flex w-full items-center justify-between rounded-2xl border bg-white/70 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-200 outline-none dark:bg-zinc-950/70 dark:shadow-[0_1px_2px_rgba(0,0,0,0.35)]",
          isFilter ? "h-11 px-3 py-0 text-xs text-foreground" : "h-10 px-3 py-0 text-xs text-foreground",
          "focus:ring-1 focus:ring-offset-0",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "[&>span]:line-clamp-1 [&>span]:flex [&>span]:items-center",
          colors.border,
          colors.focusBorder,
          colors.focusRing,
        )}
        {...props}
        onChange={() => setHasValue(true)}
      >
        {children}
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      {label && (
        <span
          className={cn(
            "pointer-events-none absolute left-3 select-none font-medium transition-all duration-200",
            hasValue || floating
              ? cn("top-0 -translate-y-1/2 rounded-2xl bg-white/90 px-1.5 py-0.5 text-[10px] leading-none dark:bg-zinc-950/90", colors.label)
              : "top-1/2 -translate-y-1/2 text-xs text-muted-foreground"
          )}
        >
          {label}
        </span>
      )}
    </div>
  );
});
SelectTrigger.displayName = "SelectTrigger";

/* ─── Scroll buttons ─── */
const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>, "className" | "style">
>(({ ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className="flex cursor-default items-center justify-center py-1"
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>, "className" | "style">
>(({ ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className="flex cursor-default items-center justify-center py-1"
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

/* ─── Content ─── */
const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>, "className" | "style">
>(({ children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "max-h-96 min-w-[8rem] overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 text-popover-foreground shadow-[0_12px_30px_-18px_rgba(15,23,42,0.35)] backdrop-blur-sm dark:border-zinc-800 dark:bg-zinc-950/95 dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1"
      )}
      position={position}
      style={{ zIndex: 12001 }}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

/* ─── Label ─── */
const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>, "className" | "style">
>(({ ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className="px-2 py-1.5 text-xs font-semibold text-muted-foreground"
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

/* ─── Item ─── */
const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>, "className" | "style">
>(({ children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={[
      "relative flex w-full cursor-default select-none items-center rounded-xl py-1.5 pl-7 pr-2 text-xs outline-none transition-colors",
      "focus:bg-accent/75 focus:text-accent-foreground",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
    ].join(" ")}
    {...props}
  >
    <span className="absolute left-2 flex h-3 w-3 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-3.5 w-3.5 text-primary" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

/* ─── Separator ─── */
const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>, "className" | "style">
>(({ ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className="-mx-1 my-1 h-px bg-muted"
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

/* ─── Field wrapper ─── */
interface SelectFieldProps {
  error?: string;
  helperText?: string;
  children: React.ReactNode;
}

function SelectField({ error, helperText, children }: SelectFieldProps) {
  return (
    <div className="w-full">
      {children}
      {(error ?? helperText) && (
        <p className={cn("mt-1.5 px-1 text-xs", error ? "text-destructive" : "text-muted-foreground")}>
          {error ?? helperText}
        </p>
      )}
    </div>
  );
}

export {
  Select,
  SelectContent,
  SelectField,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
